import { gql, Resolver } from '@apollo/client'; // eslint-disable-line

import { generateUuidFileKeyForS3 } from '@trqk/utils';

import { uploadFile } from '../s3/helpers';

interface Args {
    bucketName: string;
    bucketRegion: string;
    file: File | S3UploadFile;
    key?: string;
    doTaskUpdates?: DoTaskUpdates;
    identityPoolId: string;
    taskId: string;
    progressOffset?: number;
    progressShare?: number;
}

interface TaskUploadToS3Context {
    bindManagedUpload?: BindManagedUpload;
    generateS3Key?(file: File): string;
    sendTaskEvent: SendTaskEvent;
}

/**
 * Uploads a file to S3.
 * Accepts an `args.doTaskUpdates` config that determines in which cases the updates will be sent to
 * the TaskProvider state machine using context.sendTaskEvent().
 *
 * Uses args.progressShare in order to calculate the total progress for a task, which may include
 * other steps. This allows the s3 upload to take a given portion of the task progress. The
 * remainder of the task progress can then be allotted to a different task reducer. For an example,
 * see taskUploadAndIngestProStatement.
 */
export const taskUploadToS3: Resolver = async (
    obj: Record<string, unknown>,
    args: Args,
    context: TaskUploadToS3Context,
): Promise<UploadToS3TaskOutput> => {
    const {
        bucketName,
        bucketRegion,
        file,
        key,
        identityPoolId,
        progressOffset = 0,
        progressShare = 100,
        taskId,
    } = args;

    const doTaskUpdates = args?.doTaskUpdates ?? {
        error: true,
        progress: true,
        success: true,
    };

    const {
        bindManagedUpload,
        // If the context does not have a keyGenerator, use uuid() to generate a key.
        generateS3Key = generateUuidFileKeyForS3,
        sendTaskEvent,
    } = context;

    try {
        const uploadResult = await uploadFile({
            bindManagedUpload,
            bucketName,
            bucketRegion,
            file,
            key,
            generateS3Key,
            handleProgress: (s3Progress: S3UploadProgress): void => {
                if (doTaskUpdates.progress) {
                    const loaded = s3Progress?.loaded ?? -1;
                    const total = s3Progress?.total ?? -1;

                    if (loaded > -1 && total > -1) {
                        const progress =
                            progressOffset + (loaded / total) * progressShare;

                        sendTaskEvent({
                            type: 'PROGRESS',
                            value: {
                                taskId,
                                progress,
                            },
                        });
                    }
                }
            },
            identityPoolId,
            taskId,
        });

        const filename = uploadResult.result?.filename ?? null;
        const resultKey = uploadResult.result?.key ?? null;
        const location = uploadResult.result?.location ?? null;

        if (doTaskUpdates.success) {
            sendTaskEvent({
                type: 'SUCCESS',
                value: {
                    taskId,
                    output: {
                        filename,
                        key: resultKey,
                        location,
                    },
                },
            });
        }

        return {
            aborted: uploadResult.aborted,
            completed: uploadResult.completed,
            filename,
            fileSizeBytes: file.size,
            key: resultKey,
            location,
            taskId,
        };
    } catch (e) {
        const error = e as Error;

        const isRequestAbortedError = error
            .toString()
            .includes('RequestAbortedError');

        if (isRequestAbortedError) {
            return {
                aborted: true,
                completed: false,
                filename: file.name,
                fileSizeBytes: file.size,
                key: null,
                location: null,
                taskId,
            };
        }

        if (doTaskUpdates.error) {
            sendTaskEvent({
                type: 'ERROR',
                value: {
                    taskId,
                    error: `Failed to upload file to S3: ${file.name}`,
                },
            });
        }

        throw error;
    }
};

export const TASK_UPLOAD_TO_S3 = gql`
    mutation TaskUploadToS3(
        $bucketName: String!
        $bucketRegion: String!
        $doTaskUpdates: DoTaskUpdates
        $file: Upload!
        $identityPoolId: String!
        $progressShare: Number
        $taskId: ID!
    ) {
        taskUploadToS3(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            doTaskUpdates: $doTaskUpdates
            file: $file
            identityPoolId: $identityPoolId
            progressShare: $progressShare
            taskId: $taskId
        ) @client
    }
`;
